import { useEffect } from "react";

import { ErrorBoundary } from "react-error-boundary";
import { useDispatch, useSelector } from "react-redux";

import { CONTACTS_SEARCH_RESULTS_LOWER_LIMIT } from "app/pages/sales-intelligence/modules/company-analysis/constants/contacts";
import companyAnalysisTrackingService from "app/pages/sales-intelligence/modules/company-analysis/services/tracking/companyAnalysisTrackingService";
import {
    contactsTableFiltersChangedAction,
    profileModalIsOpenedSetAction,
    profileSelectedAction,
    setProfileForEditAction,
} from "app/pages/sales-intelligence/modules/company-analysis/store/contacts/action-creators";
import { selectActiveProfileId } from "app/pages/sales-intelligence/modules/company-analysis/store/contacts/selectors";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import {
    selectContactsResultsAreBeingFetched,
    selectNumberOfContactsSearchResults,
} from "app/pages/sales-intelligence/sub-modules/contacts-search/store/selectors";
import { fetchProfilesThunk } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/store/effects";
import type { ProfileDto } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/types/dto";
import { selectLoadingContactStatus } from "app/pages/sales-intelligence/sub-modules/contacts-tab/store/selectors";

import EmployeeProfilesDropdown from "./EmployeeProfilesDropdown";
import useProfilesByAccTechSubCategory from "./useProfilesByAccTechSubCategory";
import { CONTACTS_SEARCH_TABLE_INITIAL_PAGE } from "app/pages/sales-intelligence/sub-modules/contacts-search/constants/table";

const EmployeeProfilesDropdownContainer = () => {
    const dispatch = useDispatch();

    const profiles = useProfilesByAccTechSubCategory();

    // Champion is treated like Admin for shared authoring, same convention as the Personas page.
    const canManageShared = salesSettingsHelper.isHighLevelAccount();

    const selectedProfileId = useSelector(selectActiveProfileId);
    const isLoadingContactsResults = useSelector(selectContactsResultsAreBeingFetched);
    const isLoadingContactStatus = useSelector(selectLoadingContactStatus);

    const numberOfSearchResults = useSelector(selectNumberOfContactsSearchResults);

    useEffect(() => {
        void dispatch(fetchProfilesThunk());
    }, []);

    function handleProfileSelect(profile: ProfileDto | null) {
        if (profile !== null && numberOfSearchResults > CONTACTS_SEARCH_RESULTS_LOWER_LIMIT) {
            companyAnalysisTrackingService.contacts.trackEmployeeProfileSelected(profile.isShared);
        }

        dispatch(profileSelectedAction(profile?.id ?? null));
        dispatch(
            contactsTableFiltersChangedAction({
                page: CONTACTS_SEARCH_TABLE_INITIAL_PAGE,
            }),
        );
    }

    function handleCreateProfileClick() {
        if (numberOfSearchResults > CONTACTS_SEARCH_RESULTS_LOWER_LIMIT) {
            companyAnalysisTrackingService.contacts.trackEmployeeProfileCreateClicked();
        }

        dispatch(profileModalIsOpenedSetAction(true));
    }

    function handleEditProfileClick(profile: ProfileDto) {
        if (numberOfSearchResults > CONTACTS_SEARCH_RESULTS_LOWER_LIMIT) {
            companyAnalysisTrackingService.contacts.trackEmployeeProfileEditClicked(profile.name);
        }

        dispatch(setProfileForEditAction(profile));
    }

    function handleOpen() {
        if (numberOfSearchResults > CONTACTS_SEARCH_RESULTS_LOWER_LIMIT) {
            companyAnalysisTrackingService.contacts.trackEmployeeProfilesDropdownOpened();
        }
    }

    return (
        <ErrorBoundary fallbackRender={() => null}>
            <EmployeeProfilesDropdown
                profiles={profiles}
                isDisabled={isLoadingContactStatus || isLoadingContactsResults || numberOfSearchResults === 0}
                selectedProfileId={selectedProfileId}
                canManageShared={canManageShared}
                onSelect={handleProfileSelect}
                onItemSettingsClick={handleEditProfileClick}
                onCreateClick={handleCreateProfileClick}
                onOpen={handleOpen}
            />
        </ErrorBoundary>
    );
};

export default EmployeeProfilesDropdownContainer;
